import Modal from "./top/modal.es6";
import { BrushReveal } from "./fate-zero-vote/brush-reveal.es6";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const keyvisual = document.querySelector(".js-vote-kv");
const keyvisualImage = keyvisual && keyvisual.querySelector(".js-vote-kv-image");
const keyvisualCanvas = keyvisual && keyvisual.querySelector(".js-vote-kv-canvas");
const mv = document.querySelector(".js-vote-mv");
const pageLoaded = new Promise((resolve) => {
    if (document.body.classList.contains("-load")) {
        resolve();
        return;
    }

    const loadObserver = new MutationObserver(() => {
        if (!document.body.classList.contains("-load")) return;
        loadObserver.disconnect();
        resolve();
    });
    loadObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
});
let keyvisualRevealComplete = Promise.resolve();

if (keyvisual && keyvisualImage && keyvisualCanvas) {
    const completeReveal = () => {
        keyvisual.classList.add("-done");
        return new Promise((resolve) => {
            setTimeout(() => {
                keyvisual.classList.add("-canvas-hidden");
                resolve();
            }, 120);
        });
    };
    const startReveal = async () => {
        let scaleStartTimer = null;

        try {
            const isWide = window.matchMedia("(min-width: 1025px)").matches;
            const scaleLeadTime = 200;
            const reveal = new BrushReveal(keyvisualCanvas, {
                src: keyvisualImage.currentSrc || keyvisualImage.src,
                delay: isWide ? 1000 : 0,
                gradientEnd: window.matchMedia("(min-width: 800px)").matches ? 0.7 : 0.6,
            });
            keyvisual.style.setProperty("--vote-kv-scale-duration", `${reveal.opt.duration}ms`);
            const imageDecode = typeof keyvisualImage.decode === "function" ? keyvisualImage.decode().catch(() => {}) : Promise.resolve();
            const assetsReady = reveal.load().then(
                () => true,
                () => false
            );
            await pageLoaded;
            if (!(await assetsReady)) return;
            const scaleStartDelay = Math.max(reveal.opt.delay - scaleLeadTime, 0);
            if (scaleStartDelay === 0) {
                keyvisual.classList.add("-revealing");
            } else {
                scaleStartTimer = setTimeout(() => keyvisual.classList.add("-revealing"), scaleStartDelay);
            }
            await reveal.play();
            await imageDecode;
        } catch {
            // Canvasや画像読み込みを利用できない場合もKV自体は表示する
        } finally {
            clearTimeout(scaleStartTimer);
            await completeReveal();
        }
    };
    keyvisualRevealComplete = startReveal();
}

const waitForMvIntro = async () => {
    await pageLoaded;
    await keyvisualRevealComplete;
    const introElements = [mv, document.querySelector(".js-vote-recommend-link")].filter(Boolean);
    let animations;
    do {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        animations = introElements
            .flatMap((element) => element.getAnimations({ subtree: true }))
            .filter((animation) => animation.playState !== "finished" && animation.playState !== "idle" && Number.isFinite(animation.effect.getComputedTiming().iterations));
        await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
    } while (animations.length);
};

const mvSceneElements = [...document.querySelectorAll(".js-vote-mv-scene")];

if (mvSceneElements.length) {
    const wideMedia = window.matchMedia("(min-width: 1025px)");
    const episodeNumbers = Array.from({ length: 25 }, (_, index) => String(index + 1));
    const scenes = mvSceneElements.map((element) => ({
        element,
        images: [...element.querySelectorAll(".js-vote-mv-scene-image")],
        activeIndex: 0,
    }));
    let changeTimer = null;
    let lastChangedIndexes = new Set();

    const sceneClass = (number) => `p-vote-mv__scene-image--${number}`;
    const clearImage = (image) => {
        if (image.dataset.voteScene) image.classList.remove(sceneClass(image.dataset.voteScene));
        image.dataset.voteScene = "";
    };
    const setImage = (image, number) => {
        clearImage(image);
        image.classList.add(sceneClass(number));
        image.dataset.voteScene = number;
    };
    const canAnimateScenes = () => !document.hidden && wideMedia.matches;
    const randomDelay = (min, max) => min + Math.random() * (max - min);
    const restoreInitialScenes = () => {
        clearTimeout(changeTimer);
        scenes.forEach((scene) => {
            scene.images.forEach((image) => gsap.killTweensOf(image));
            setImage(scene.images[0], scene.element.dataset.voteInitialScene);
            clearImage(scene.images[1]);
            gsap.set(scene.images[0], { opacity: 1, filter: "none", scale: 1 });
            gsap.set(scene.images[1], { opacity: 0, filter: "none", scale: 1 });
            scene.activeIndex = 0;
        });
        lastChangedIndexes = new Set();
    };
    const scheduleSceneChange = (delay = randomDelay(2050, 3000)) => {
        clearTimeout(changeTimer);
        if (!canAnimateScenes()) return;
        changeTimer = setTimeout(changeScenes, delay);
    };
    function changeScenes() {
        if (!canAnimateScenes()) return;

        const visibleScenes = scenes.map((scene, index) => ({ scene, index })).filter(({ scene }) => scene.element.getClientRects().length);
        const nextCandidates = visibleScenes.filter(({ index }) => !lastChangedIndexes.has(index));
        const candidates = nextCandidates.length >= 2 ? nextCandidates : visibleScenes;
        const changeCount = Math.min(candidates.length, 3);
        if (!changeCount) {
            scheduleSceneChange();
            return;
        }

        const targets = [...candidates].sort(() => Math.random() - 0.5).slice(0, changeCount);
        const usedNumbers = new Set(scenes.map((scene) => scene.images[scene.activeIndex].dataset.voteScene).filter(Boolean));
        lastChangedIndexes = new Set(targets.map(({ index }) => index));

        targets.forEach(({ scene }) => {
            const currentImage = scene.images[scene.activeIndex];
            const nextIndex = scene.activeIndex === 0 ? 1 : 0;
            const nextImage = scene.images[nextIndex];
            const availableNumbers = episodeNumbers.filter((number) => !usedNumbers.has(number));
            const nextNumber = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
            usedNumbers.add(nextNumber);
            setImage(nextImage, nextNumber);
            gsap.set(nextImage, { opacity: 0, filter: "none", scale: 1 });
            gsap.to(currentImage, {
                opacity: 0,
                duration: 2,
                ease: "power2.in",
                onComplete: () => {
                    clearImage(currentImage);
                },
            });
            gsap.to(nextImage, {
                opacity: 1,
                duration: 2,
                ease: "power2.out",
            });
            scene.activeIndex = nextIndex;
        });
        scheduleSceneChange();
    }

    wideMedia.addEventListener("change", () => {
        restoreInitialScenes();
        if (wideMedia.matches) scheduleSceneChange();
    });
    document.addEventListener("visibilitychange", () => {
        clearTimeout(changeTimer);
        if (!document.hidden && wideMedia.matches) scheduleSceneChange();
    });
    restoreInitialScenes();
    if (wideMedia.matches) scheduleSceneChange();
}

const dialogs = [...document.querySelectorAll(".js-vote-modal")].map((dialog) => ({
    dialog,
    close: dialog.querySelector(".js-modal_close"),
    opener: null,
    parentState: null,
    closing: false,
    allowClose: false,
    openFrame: null,
}));

const resultsSection = document.querySelector(".js-vote-results");
const recommendLink = document.querySelector(".js-vote-recommend-link");

const revealResultCard = (card, delay = 0) => {
    const canvas = document.createElement("canvas");
    let reveal;
    const cleanup = () => {
        if (reveal) reveal.stop();
        canvas.remove();
        card.classList.remove("-reveal-ready");
    };
    try {
        const width = card.clientWidth;
        const height = card.clientHeight;
        if (!width || !height) {
            cleanup();
            return cleanup;
        }
        canvas.className = "p-vote-results__brush";
        canvas.setAttribute("aria-hidden", "true");
        card.appendChild(canvas);
        reveal = new BrushReveal(canvas, {
            width: 613,
            height: (613 * height) / width,
            delay,
            bristleWidth: [8, 20],
            gradientOpacity: 0,
            coverColor: getComputedStyle(card).getPropertyValue("--result-card-bg").trim(),
            onStart: () => card.classList.remove("-reveal-ready"),
        });
        reveal.play().then(cleanup, cleanup);
    } catch {
        // Canvasを利用できない場合も結果を表示する
        cleanup();
    }
    return cleanup;
};

if (resultsSection) {
    resultsSection.querySelectorAll(".js-vote-result-row").forEach((row) => {
        const cards = [...row.querySelectorAll(".js-vote-result-card")];
        cards.forEach((card) => card.classList.add("-reveal-ready"));
        try {
            gsap.set(row, { opacity: 0 });
            ScrollTrigger.create({
                trigger: row,
                start: "top 92%",
                once: true,
                onEnter: () =>
                    gsap.to(row, {
                        opacity: 1,
                        duration: 0.2,
                        ease: "power1.out",
                        clearProps: "opacity",
                        onComplete: () => cards.forEach((card) => revealResultCard(card)),
                    }),
            });
        } catch {
            gsap.set(row, { clearProps: "opacity" });
            cards.forEach((card) => card.classList.remove("-reveal-ready"));
        }
    });
}

if (dialogs.length) {
    const modalTransitionDuration = 200;
    let activeState = null;
    const dialogMap = new Map(dialogs.map((state) => [state.dialog.dataset.voteEpisode, state]));
    const openButtons = [...document.querySelectorAll(".js-vote-open")];
    const resultState = dialogMap.get("results");
    const resultRevealElements = resultState ? [...resultState.dialog.querySelectorAll(".js-vote-result-reveal")] : [];
    const resultModalRows = resultState ? [...resultState.dialog.querySelectorAll(".js-vote-result-row")] : [];
    const resultModalCards = resultState ? [...resultState.dialog.querySelectorAll(".js-vote-result-card")] : [];
    let resultRevealDelay = null;
    let resultRowTriggers = [];
    let resultRowTweens = [];
    let resultCardCleanups = [];
    const resetResultModalReveal = () => {
        if (resultRevealDelay) resultRevealDelay.kill();
        resultRevealDelay = null;
        resultRowTriggers.forEach((trigger) => trigger.kill());
        resultRowTriggers = [];
        resultRowTweens.forEach((tween) => tween.kill());
        resultRowTweens = [];
        resultCardCleanups.forEach((cleanup) => cleanup());
        resultCardCleanups = [];
        resultRevealElements.forEach((element) => element.classList.remove("-reveal"));
        resultModalCards.forEach((card) => card.classList.remove("-reveal-ready"));
        resultModalRows.forEach((row) => gsap.set(row, { clearProps: "opacity" }));
    };
    const prepareResultModalReveal = () => {
        resetResultModalReveal();
        resultModalRows.forEach((row) => gsap.set(row, { opacity: 0 }));
        resultModalCards.forEach((card) => card.classList.add("-reveal-ready"));
    };
    const startResultModalReveal = () => {
        resultRevealElements.forEach((element) => element.classList.add("-reveal"));
        if (!resultModalRows.length) return;
        resultRevealDelay = gsap.delayedCall(0.7, () => {
            resultRevealDelay = null;
            if (!resultState.dialog.open || resultState.closing) return;
            resultModalRows.forEach((row) => {
                const cards = [...row.querySelectorAll(".js-vote-result-card")];
                try {
                    const trigger = ScrollTrigger.create({
                        trigger: row,
                        scroller: resultState.dialog,
                        start: "top 92%",
                        once: true,
                        onEnter: () => {
                            if (!resultState.dialog.open || resultState.closing) return;
                            const tween = gsap.to(row, {
                                opacity: 1,
                                duration: 0.2,
                                ease: "power1.out",
                                clearProps: "opacity",
                                onComplete: () => {
                                    if (!resultState.dialog.open || resultState.closing) return;
                                    cards.forEach((card) => resultCardCleanups.push(revealResultCard(card)));
                                },
                            });
                            resultRowTweens.push(tween);
                        },
                    });
                    resultRowTriggers.push(trigger);
                } catch {
                    gsap.set(row, { clearProps: "opacity" });
                    cards.forEach((card) => card.classList.remove("-reveal-ready"));
                }
            });
        });
    };

    openButtons.forEach((button) => {
        button.addEventListener("click", (event) => {
            const state = dialogMap.get(button.dataset.voteOpen);
            const fromResults = resultState && resultState.dialog.contains(button);
            const openedStates = dialogs.filter(({ dialog }) => dialog.open);
            const canOpenOverResults = state && state !== resultState && fromResults && activeState === resultState && !resultState.closing && openedStates.length === 1 && openedStates[0] === resultState;
            if (!state || state.dialog.open || (openedStates.length && !canOpenOverResults) || (fromResults && !canOpenOverResults)) {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }
            state.parentState = canOpenOverResults ? resultState : null;
            state.opener = button;
            if (state === resultState) state.opener = recommendLink;
            state.closing = false;
            state.dialog.classList.toggle("-over-results", Boolean(state.parentState));
            state.dialog.classList.remove("-closing");
            state.dialog.classList.remove("-visible");
            activeState = state;
        });
    });

    const modal = new Modal();

    openButtons.forEach((button) => {
        button.addEventListener("click", () => {
            const state = dialogMap.get(button.dataset.voteOpen);
            if (!state || activeState !== state) return;
            if (state === resultState) prepareResultModalReveal();
            state.dialog.showModal();
            state.dialog.scrollTop = 0;
            // タップで開いた時に閉じるボタンへフォーカス枠が出ないよう、dialog本体へフォーカスする
            state.dialog.focus({ preventScroll: true });
            state.openFrame = requestAnimationFrame(() => {
                state.openFrame = null;
                if (state.dialog.open && !state.closing) {
                    state.dialog.classList.add("-visible");
                    if (state === resultState) startResultModalReveal();
                }
            });
        });
    });

    dialogs.forEach((state) => {
        const { dialog, close } = state;

        dialog.addEventListener(
            "click",
            (event) => {
                // 投票ページでは共通Modalの背景クリックによる閉じる処理を止める。
                if (!event.target.closest(".js-modal_content, .js-modal_close")) {
                    event.stopImmediatePropagation();
                }
            },
            { capture: true }
        );

        close.addEventListener(
            "click",
            (event) => {
                if (state.allowClose) {
                    state.allowClose = false;
                    return;
                }
                event.preventDefault();
                event.stopImmediatePropagation();
                if (state.closing || (activeState !== state && dialog.open)) return;
                state.closing = true;
                if (state.openFrame !== null) cancelAnimationFrame(state.openFrame);
                state.openFrame = null;
                dialog.classList.add("-closing");
                dialog.classList.remove("-visible");
                setTimeout(() => {
                    state.allowClose = true;
                    close.click();
                }, modalTransitionDuration);
            },
            { capture: true }
        );
        close.addEventListener("click", () => {
            const parentState = state.parentState;
            const parentIsOpen = parentState && parentState.dialog.open;
            if (dialog.open) dialog.close();
            if (state === resultState) resetResultModalReveal();
            dialog.classList.remove("-closing");
            dialog.classList.remove("-visible");
            dialog.classList.remove("-over-results");
            state.closing = false;
            if (activeState === state) activeState = parentIsOpen ? parentState : null;
            const opener = parentState && !parentIsOpen ? parentState.opener || recommendLink : state.opener;
            if (opener) opener.focus({ preventScroll: true });
            state.parentState = null;
        });
        dialog.addEventListener("cancel", (event) => {
            event.preventDefault();
            if (activeState === state && state === resultState) close.click();
        });
        dialog.addEventListener("keydown", (event) => {
            if (event.key !== "Tab" || activeState !== state) return;
            const buttons = [...dialog.querySelectorAll(".js-vote-focusable")].filter((button) => !button.disabled && button.getClientRects().length);
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            if (event.shiftKey && (document.activeElement === first || !buttons.includes(document.activeElement))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !buttons.includes(document.activeElement))) {
                event.preventDefault();
                first.focus();
            }
        });
        dialog.addEventListener("close", () => {
            if (!dialog.open && dialog.classList.contains("-active")) close.click();
        });
    });

    const resultTrigger = document.querySelector(".js-vote-result-trigger");
    const resultSessionKey = "mahoyo-fate-zero-vote-results-seen";
    // 顧客確認中のみtrue。本番公開時はfalseに戻してセッション単位の表示にする。
    const previewResultsEveryLoad = true;
    let resultSeen = false;
    try {
        resultSeen = sessionStorage.getItem(resultSessionKey) === "1";
    } catch {
        // 保存できない環境では初回と同じ表示を維持する。
    }
    if (resultTrigger && resultState && (previewResultsEveryLoad || !resultSeen)) {
        const scrollFunc = window.scrollFunc;
        const introScrollLock = scrollFunc && scrollFunc.acquireScrollLock("vote-mv-intro");
        if (window.lenis) {
            window.lenis.scrollTo(0, { immediate: true, force: true });
        } else {
            window.scrollTo(0, 0);
        }
        modal.lockPageScroll();
        document.body.classList.add("-vote-intro");
        const openResultsAfterIntro = async () => {
            try {
                await waitForMvIntro();
                // Lenisのロックを保ったまま、共通Modalへページ固定とスクロールバー幅の計測を引き継ぐ。
                document.body.classList.remove("-vote-intro");
                modal.unlockPageScroll();
                resultTrigger.click();
                if (resultState.dialog.open) {
                    try {
                        sessionStorage.setItem(resultSessionKey, "1");
                    } catch {
                        // 保存できない場合もモーダルの操作とスクロール解除は続行する。
                    }
                }
            } finally {
                document.body.classList.remove("-vote-intro");
                if (!dialogs.some(({ dialog }) => dialog.open)) modal.unlockPageScroll();
                if (scrollFunc) {
                    scrollFunc.releaseScrollLock(introScrollLock);
                    scrollFunc.refresh();
                }
            }
        };
        openResultsAfterIntro();
    }
}
